import { Button, Card, CardContent, CardHeader, CardTitle, Spinner, useFeatureFlag } from '@lensora/ui'
import { KeyRound, LineChart, Plus, ShieldAlert } from 'lucide-react'
import * as React from 'react'
import { Link } from 'react-router-dom'

import { CreateKeyModal } from '@/components/mcp/CreateKeyModal'
import { KeyRow } from '@/components/mcp/KeyRow'
import { KeyUsageModal } from '@/components/mcp/KeyUsageModal'
import { McpUpsellCard } from '@/components/mcp/McpUpsellCard'
import { SecretRevealModal } from '@/components/mcp/SecretRevealModal'
import { useClients } from '@/hooks/use-clients'
import type { CreateMcpKeyResult, McpApiKey } from '@/hooks/use-mcp-keys'
import { useMcpKeys } from '@/hooks/use-mcp-keys'
import { useMe } from '@/hooks/use-me'

// ─── Sub-components ────────────────────────────────────────────

function NoPermission() {
  return (
    <Card className="border-feedback-warning/30 bg-feedback-warning/5">
      <CardContent className="flex items-start gap-3 pt-6">
        <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-feedback-warning" />
        <p className="text-sm text-text">
          Only workspace owners and admins can manage Lenora MCP keys.
        </p>
      </CardContent>
    </Card>
  )
}

function EmptyKeysState({ onCreate, canCreate }: { onCreate: () => void; canCreate: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <KeyRound className="mb-4 h-12 w-12 text-text-muted opacity-40" />
      <p className="font-medium text-text">No MCP keys yet</p>
      <p className="mt-1 max-w-sm text-sm text-text-muted">
        Create a key to let Claude, ChatGPT, Cursor, or n8n connect to this workspace's Lenora data.
      </p>
      {canCreate && (
        <Button className="mt-4" onClick={onCreate}>
          <Plus className="mr-2 h-4 w-4" />Create key
        </Button>
      )}
    </div>
  )
}

// ─── Page ──────────────────────────────────────────────────────

export function IntegrationsPage() {
  const { data: me, isLoading: meLoading } = useMe()
  const role = me?.user.role
  const isOwnerOrAdmin = role === 'owner' || role === 'admin'
  const mcpEnabled = useFeatureFlag('mcp_enabled')

  const { data: keys, isLoading: keysLoading } = useMcpKeys(isOwnerOrAdmin)
  const { data: endClients } = useClients(isOwnerOrAdmin)

  const [showCreate, setShowCreate] = React.useState(false)
  const [revealed, setRevealed] = React.useState<CreateMcpKeyResult | null>(null)
  const [usageKey, setUsageKey] = React.useState<McpApiKey | null>(null)

  if (meLoading) {
    return <div className="flex h-full items-center justify-center"><Spinner /></div>
  }

  if (!isOwnerOrAdmin) {
    return (
      <div className="mx-auto max-w-2xl space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-text">Lenora MCP</h2>
          <p className="mt-1 text-sm text-text-muted">Manage API keys for external MCP clients.</p>
        </div>
        <NoPermission />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {showCreate && (
        <CreateKeyModal
          endClients={endClients ?? []}
          onClose={() => setShowCreate(false)}
          onCreated={(result) => { setShowCreate(false); setRevealed(result) }}
        />
      )}
      {revealed && (
        <SecretRevealModal
          keyName={revealed.key.name}
          keyPrefix={revealed.key.keyPrefix}
          secret={revealed.secret}
          onDismiss={() => setRevealed(null)}
        />
      )}
      {usageKey && (
        <KeyUsageModal keyId={usageKey.id} keyName={usageKey.name} onClose={() => setUsageKey(null)} />
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-text">Lenora MCP</h2>
          <p className="mt-1 text-sm text-text-muted">
            Connect Claude, ChatGPT, Cursor, or n8n to this workspace's Lenora data.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" asChild>
            <Link to="/dashboard/settings/looker">
              <LineChart className="mr-2 h-4 w-4" />Looker Studio
            </Link>
          </Button>
          {mcpEnabled && (
            <Button onClick={() => setShowCreate(true)}>
              <Plus className="mr-2 h-4 w-4" />Create key
            </Button>
          )}
        </div>
      </div>

      {!mcpEnabled && <McpUpsellCard />}

      {keysLoading ? (
        <div className="flex h-full items-center justify-center"><Spinner /></div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>API keys</CardTitle>
          </CardHeader>
          <CardContent className="divide-y divide-border p-0">
            {(keys ?? []).length === 0 ? (
              <EmptyKeysState onCreate={() => setShowCreate(true)} canCreate={mcpEnabled} />
            ) : (
              (keys ?? []).map((k) => <KeyRow key={k.id} mcpKey={k} onViewUsage={setUsageKey} />)
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
